import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, ApiError, storage } from '../api'
import { type MessageKey, useI18n } from '../i18n'
import type { CatalogRef, MediaInfo, SubtitleResult, SubtitleTrack } from '../protocol'
import { Icon } from '../ui/Icon'

const LANGS_KEY = 'chillio.subtitleLanguages'
const LANGUAGES = ['ru', 'en', 'uk', 'de', 'fr', 'es', 'it', 'pt', 'pt-br', 'pl', 'cs', 'tr', 'ja', 'ko', 'zh']

const problems: Record<string, MessageKey> = {
  no_target: 'subsearch.noTarget',
  quota: 'subsearch.quota',
  expired: 'subsearch.expired',
}

interface Props {
  roomId: string
  title: CatalogRef | null
  media: MediaInfo
  /** A downloaded track, to turn on for whoever added it. */
  onAdded: (track: SubtitleTrack) => void
  onClose: () => void
}

/**
 * Searches OpenSubtitles for the room's title (and the video file itself)
 * and adds a subtitle file to the media for everyone.
 */
export function SubtitleSearchDialog({ roomId, title, media, onAdded, onClose }: Props) {
  const { t, lang } = useI18n()
  const series = title?.type === 'tv'
  const guessed = useMemo(() => parseEpisode(media.name), [media.name])
  const [langs, setLangs] = useState<string[]>(() => savedLanguages(lang))
  const [season, setSeason] = useState(() => String(guessed.season ?? title?.season ?? ''))
  const [episode, setEpisode] = useState(() => String(guessed.episode ?? ''))
  const [results, setResults] = useState<SubtitleResult[] | null>(null)
  const [hashOnly, setHashOnly] = useState(false)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState<number | null>(null)
  const [error, setError] = useState('')

  const message = (e: unknown) => {
    if (e instanceof ApiError && e.code && problems[e.code]) return t(problems[e.code])
    return t('subsearch.error', { message: e instanceof Error ? e.message : String(e) })
  }

  const search = useCallback(async () => {
    setBusy(true)
    setError('')
    try {
      const r = await api.searchSubtitles(roomId, {
        languages: langs,
        season: series ? Number(season) || undefined : undefined,
        episode: series ? Number(episode) || undefined : undefined,
      })
      setResults(r.results)
      setHashOnly(r.byHash && !r.byTitle)
    } catch (e) {
      setResults(null)
      setError(message(e))
    } finally {
      setBusy(false)
    }
  }, [roomId, langs, season, episode, series]) // eslint-disable-line react-hooks/exhaustive-deps

  // Searched right away with what is known.
  useEffect(() => {
    void search()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  const toggleLang = (code: string) => {
    const next = langs.includes(code) ? langs.filter((l) => l !== code) : [...langs, code]
    if (next.length === 0) return
    setLangs(next)
    storage.set(LANGS_KEY, next.join(','))
  }

  const add = async (r: SubtitleResult) => {
    setAdding(r.fileId)
    setError('')
    try {
      const { track } = await api.addSubtitle(roomId, { fileId: r.fileId, language: r.language, release: r.release })
      onAdded(track)
      onClose()
    } catch (e) {
      setError(message(e))
    } finally {
      setAdding(null)
    }
  }

  const names = useMemo(() => {
    try {
      return new Intl.DisplayNames([lang], { type: 'language' })
    } catch {
      return null
    }
  }, [lang])
  const langName = (code: string) => {
    try {
      return names?.of(code) ?? code
    } catch {
      return code
    }
  }

  return createPortal(
    <div className="dialog" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog__card subsearch" role="dialog" aria-modal="true" aria-label={t('subsearch.title')} onKeyDown={(e) => e.stopPropagation()}>
        <header className="subsearch__head">
          <h2 className="dialog__title">{t('subsearch.title')}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('subsearch.close')}>
            <Icon name="close" />
          </button>
        </header>
        <form
          className="subsearch__form"
          onSubmit={(e) => {
            e.preventDefault()
            void search()
          }}
        >
          <div className="subsearch__langs" role="group" aria-label={t('subsearch.languages')}>
            {LANGUAGES.map((code) => (
              <button key={code} type="button" aria-pressed={langs.includes(code)} onClick={() => toggleLang(code)}>
                {langName(code)}
              </button>
            ))}
          </div>
          <div className="subsearch__row">
            {series && (
              <>
                <label className="subsearch__num">
                  <span>{t('subsearch.season')}</span>
                  <input inputMode="numeric" value={season} onChange={(e) => setSeason(e.target.value.replace(/\D/g, ''))} maxLength={3} />
                </label>
                <label className="subsearch__num">
                  <span>{t('subsearch.episode')}</span>
                  <input inputMode="numeric" value={episode} onChange={(e) => setEpisode(e.target.value.replace(/\D/g, ''))} maxLength={4} />
                </label>
              </>
            )}
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? t('subsearch.searching') : t('subsearch.search')}
            </button>
          </div>
        </form>
        {hashOnly && <p className="subsearch__hint muted">{t('subsearch.byHashOnly')}</p>}
        {error && <p className="error-text">{error}</p>}
        {results && results.length === 0 && !busy && <p className="muted subsearch__empty">{t('subsearch.empty')}</p>}
        {results && results.length > 0 && (
          <ul className="subsearch__results">
            {results.map((r) => {
              // Downloaded before for this video: turned on without spending the limit.
              const existing = media.subtitles.find((s) => s.id === `os${r.fileId}`)
              return (
              <li key={r.fileId} className={r.hashMatch ? 'is-match' : ''}>
                <div className="subsearch__info">
                  <p className="subsearch__release">
                    <span className="subsearch__lang">{r.language.toUpperCase()}</span> {r.release}
                  </p>
                  <p className="muted small">
                    {[
                      series && r.episode ? `S${r.season ?? '?'}E${r.episode}` : null,
                      t('subsearch.downloads', { n: r.downloads }),
                      r.format.toUpperCase(),
                      r.hearingImpaired ? t('subsearch.hi') : null,
                      r.machineTranslated ? t('subsearch.machine') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {r.hashMatch && (
                    <p className="subsearch__match" title={t('subsearch.hashMatchHint')}>
                      <Icon name="check" /> {t('subsearch.hashMatch')}
                    </p>
                  )}
                </div>
                {existing ? (
                  <button
                    type="button"
                    className="btn btn--small"
                    onClick={() => {
                      onAdded(existing)
                      onClose()
                    }}
                  >
                    {t('subsearch.turnOn')}
                  </button>
                ) : (
                  <button type="button" className="btn btn--small" disabled={adding !== null} onClick={() => add(r)}>
                    {adding === r.fileId ? t('subsearch.adding') : t('subsearch.add')}
                  </button>
                )}
              </li>
              )
            })}
          </ul>
        )}
        <p className="subsearch__note muted small">{t('subsearch.note')}</p>
      </div>
    </div>,
    document.fullscreenElement ?? document.body,
  )
}

function savedLanguages(ui: string): string[] {
  const saved = storage.get(LANGS_KEY)?.split(',').filter((l) => LANGUAGES.includes(l))
  if (saved?.length) return saved
  return ui === 'en' ? ['en'] : [ui, 'en'].filter((l) => LANGUAGES.includes(l))
}

/**
 * Season and episode from a file name: "S02E05", "2x05", "Episode 5",
 * "Title - 05 (1080p)".
 */
export function parseEpisode(name: string): { season?: number; episode?: number } {
  const n = name.replace(/\.[a-z0-9]{2,4}$/i, '')
  let m = n.match(/S(\d{1,2})[ ._-]?E(\d{1,4})/i)
  if (m) return { season: Number(m[1]), episode: Number(m[2]) }
  m = n.match(/(?:^|\D)(\d{1,2})x(\d{2,3})(?:\D|$)/)
  if (m) return { season: Number(m[1]), episode: Number(m[2]) }
  m = n.match(/(?:^|[^\p{L}])(?:episode|ep|серия|e)[ ._-]?(\d{1,4})(?:\D|$)/iu)
  if (m) return { episode: Number(m[1]) }
  m = n.match(/\s-\s(\d{1,4})(?:v\d)?(?:[\s.[(]|$)/)
  if (m) return { episode: Number(m[1]) }
  return {}
}
